import moment from "moment";

export const isIterableArray = (array) =>
  Array.isArray(array) && !!array.length;

// Algunas pantallas (ej. "Ingresos comunes") están registradas bajo más de
// una sección del menú (/dashboard/finance/..., /dashboard/accounting/... y
// /dashboard/funds/...) con el mismo componente. Esto deriva el prefijo
// correcto a partir de la URL actual, para que los links/redirects internos
// del componente (crear, cancelar, volver) se queden en la misma sección
// desde la que se entró, en vez de mandar siempre a /dashboard/finance.
export const getFinanceModuleBase = (pathname = "") => {
  if (pathname.startsWith("/dashboard/accounting")) return "accounting";
  if (pathname.startsWith("/dashboard/funds")) return "funds";
  return "finance";
};

//= ==============================
// Breakpoints
//= ==============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540,
};

//= ==============================
// Date
//= ==============================
export const formatDate = (strDate) => {
  const date = new Date(strDate);

  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

// Los meses en espanol, escritos aca a proposito. El idioma de moment es
// global y termina dependiendo de que modulo se cargo antes, asi que una
// pantalla podia mostrar "03 Aug 2026" y otra "03 ago 2026" sin que nadie
// tocara nada. La fecha de un gasto sale siempre igual con esta tabla.
const MESES_CORTOS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

/**
 * La fecha de un gasto, como "03 ago 2026".
 *
 * Lee la cadena YYYY-MM-DD que manda el backend en vez de construir un Date:
 * `new Date("2026-08-03")` se interpreta como medianoche UTC y en Venezuela,
 * que va cuatro horas atras, se leeria como el 2 de agosto.
 */
export const formatExpenseDate = (value) => {
  if (!value) return "-";

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!match) return "-";

  const [, year, month, day] = match;
  const mes = MESES_CORTOS[Number(month) - 1];

  return mes ? `${day} ${mes} ${year}` : "-";
};

export const getDate = (date) => {
  const d = new Date(date);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

//= ==============================
// Store
//= ==============================
export const getItemFromStore = (key, defaultValue, store = localStorage) =>
  JSON.parse(store.getItem(key)) || defaultValue;
export const setItemToStore = (key, payload, store = localStorage) =>
  store.setItem(key, JSON.stringify(payload));
export const getStoreSpace = (store = localStorage) =>
  parseFloat(
    (
      escape(encodeURIComponent(JSON.stringify(store))).length /
      (1024 * 1024)
    ).toFixed(2)
  );

//= ==============================
// Cookie
//= ==============================
export const getCookieValue = (name) => {
  const value = document.cookie.match(
    "(^|[^;]+)\\s*" + name + "\\s*=\\s*([^;]+)"
  );
  return value ? value.pop() : null;
};

export const createCookie = (name, value, cookieExpireTime) => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + value + expires + "; path=/";
};

//= ==============================
// Moment
//= ==============================
export const getDuration = (startDate, endDate) => {
  if (!moment.isMoment(startDate)) {
    throw new Error(
      `Start date must be a moment object, received ${typeof startDate}`
    );
  }
  if (endDate && !moment.isMoment(endDate)) {
    throw new Error(
      `End date must be a moment object, received ${typeof startDate}`
    );
  }

  return `${startDate.format("ll")} - ${
    endDate ? endDate.format("ll") : "Present"
  } • ${startDate.from(endDate || moment(), true)}`;
};

export const numberFormatter = (number, fixed = 2) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + "B"
    : Math.abs(Number(number)) >= 1.0e6
    ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + "M"
    : Math.abs(Number(number)) >= 1.0e3
    ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + "K"
    : Math.abs(Number(number)).toFixed(fixed);
};

//= ==============================
// Colors
//= ==============================
export const hexToRgb = (hexValue) => {
  let hex;
  hexValue.indexOf("#") === 0
    ? (hex = hexValue.substring(1))
    : (hex = hexValue);
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
  );
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : null;
};

export const rgbColor = (color = colors[0]) => `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color = colors[0], alpha = 0.5) =>
  `rgba(${hexToRgb(color)},${alpha})`;

export const colors = [
  "#9500C6",
  "#00d97e",
  "#e63757",
  "#39afd1",
  "#fd7e14",
  "#02a8b5",
  "#727cf5",
  "#6b5eae",
  "#ff679b",
  "#f6c343",
];

export const themeColors = {
  primary: "#9500C6",
  secondary: "#748194",
  success: "#00d27a",
  info: "#27bcfd",
  warning: "#f5803e",
  danger: "#e63757",
  light: "#f9fafd",
  dark: "#0b1727",
};

export const grays = {
  white: "#fff",
  100: "#f9fafd",
  200: "#edf2f9",
  300: "#d8e2ef",
  400: "#b6c1d2",
  500: "#9da9bb",
  600: "#748194",
  700: "#5e6e82",
  800: "#4d5969",
  900: "#344050",
  1000: "#232e3c",
  1100: "#0b1727",
  black: "#000",
};

export const darkGrays = {
  white: "#fff",
  1100: "#f9fafd",
  1000: "#edf2f9",
  900: "#d8e2ef",
  800: "#b6c1d2",
  700: "#9da9bb",
  600: "#748194",
  500: "#5e6e82",
  400: "#4d5969",
  300: "#344050",
  200: "#232e3c",
  100: "#0b1727",
  black: "#000",
};

export const getGrays = (isDark) => (isDark ? darkGrays : grays);

export const rgbColors = colors.map((color) => rgbColor(color));
export const rgbaColors = colors.map((color) => rgbaColor(color));

//= ==============================
// Echarts
//= ==============================
export const getPosition = (pos, params, dom, rect, size) => ({
  top: pos[1] - size.contentSize[1] - 10,
  left: pos[0] - size.contentSize[0] / 2,
});

//= ==============================
// E-Commerce
//= ==============================
export const calculateSale = (base, less = 0, fix = 2) =>
  (base - base * (less / 100)).toFixed(fix);
export const getTotalPrice = (cart, baseItems) =>
  cart.reduce((accumulator, currentValue) => {
    const { id, quantity } = currentValue;
    const { price, sale } = baseItems.find((item) => item.id === id);
    return accumulator + calculateSale(price, sale) * quantity;
  }, 0);

//= ==============================
// Helpers
//= ==============================
export const getPaginationArray = (totalSize, sizePerPage) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const capitalize = (str) =>
  (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, " ");

export const routesSlicer = ({ routes, columns = 3, rows }) => {
  const routesCollection = [];
  routes.map((route) => {
    if (route.children) {
      return route.children.map((item) => {
        if (item.children) {
          return routesCollection.push(...item.children);
        }
        return routesCollection.push(item);
      });
    }
    return routesCollection.push(route);
  });

  const totalRoutes = routesCollection.length;
  const calculatedRows = rows || Math.ceil(totalRoutes / columns);
  const routesChunks = [];
  for (let i = 0; i < totalRoutes; i += calculatedRows) {
    routesChunks.push(routesCollection.slice(i, i + calculatedRows));
  }
  return routesChunks;
};

export const getPageName = (pageName) => {
  return window.location.pathname.split("/").slice(-1)[0] === pageName;
};

export const APP_SETTINGS = {
  API_URL: `${import.meta.env.VITE_API_URL}/api`,
};

// Teléfono venezolano: elimina el 0 inicial si existe,
// dejando siempre los 10 dígitos reales para guardar (ej: 0424… → 424…)
export const cleanVePhone = (val) => {
  if (!val) return '';
  let digits = String(val).replace(/\D/g, '');
  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }
  return digits.substring(0, 10);
};

// Máscara dinámica:
// - Si empieza por '0': +58 (0424) 345-6789 -> (4 dígitos en el paréntesis: 0424)
// - Si NO empieza por '0': +58 (424) 345-6789 -> (3 dígitos en el paréntesis: 424)
export const getVePhoneFormat = (val) => {
  const str = String(val || '').replace(/\D/g, '');
  return str.startsWith('0') ? '+58 (####) ###-####' : '+58 (###) ###-####';
};

export const VE_PHONE_FORMAT = '+58 (###) ###-####';
export const displayVePhone = (val) => cleanVePhone(val);



export function getParams(parametros) {
  if (!parametros) return "";
  const cadenaDeConsulta = parametros
    ?.filter((val) => val.value !== "" && val.value !== undefined && val)
    ?.map(({ key, value }) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return `?${cadenaDeConsulta}`;
}

